/**
 * Auth state. Spec 4.3: "AuthProvider context, hydrated from GET /api/auth/session,
 * mirrored to Query."
 *
 * In practice the hydration source is `identity.me`, not Better Auth's raw session
 * endpoint: spec 8.2 defines `identity.me` as "current user + role + guest flag", which is
 * exactly what the guards need, and it returns our DTO rather than the library's shape.
 */
import type { IdentityDto } from '@lila/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';

import { trpcClient } from '../../lib/trpc';

import { authApi } from './api';

/** One key, so a guard and a component never disagree about who is signed in. */
export const identityQueryKey = ['identity', 'me'] as const;

/**
 * `identity.me` requires a session, so an anonymous visitor gets a 401. That is the normal
 * answer for a public page, not an error — it resolves to `null` rather than throwing, so
 * every caller does not have to special-case it.
 */
export const identityQueryOptions = {
  queryKey: identityQueryKey,
  queryFn: async (): Promise<IdentityDto | null> => {
    try {
      return await trpcClient.identity.me.query();
    } catch {
      return null;
    }
  },
  /* The identity changes only when the user acts, and every such action invalidates it. */
  staleTime: 5 * 60_000,
  retry: false,
} as const;

export function ensureIdentity(queryClient: QueryClient): Promise<IdentityDto | null> {
  /*
   * `query` is the current API — `ensureQueryData` and `fetchQuery` are both deprecated in
   * TanStack Query 5.102. With the `staleTime` set on the options above, a cached identity
   * is returned without a network round trip, so every guard in one navigation shares a
   * single request.
   */
  return queryClient.query(identityQueryOptions);
}

export function useIdentity(): {
  identity: IdentityDto | null;
  isLoading: boolean;
} {
  const { data, isPending } = useQuery(identityQueryOptions);
  return { identity: data ?? null, isLoading: isPending };
}

/* Inferred: the return is TanStack Query's result type parameterised by the DTO. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useProviders() {
  return useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: () => trpcClient.identity.providers.query(),
    /* Which providers exist changes only on deploy. */
    staleTime: Infinity,
  });
}

export function useSignOut(): { signOut: () => void; isPending: boolean } {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  /* `strict: false` — the hook is used from several layouts, so read `lang` loosely. */
  const { lang } = useParams({ strict: false });

  const mutation = useMutation({
    mutationFn: () => authApi.signOut(),
    onSuccess: async () => {
      /*
       * Navigate to the public landing page BEFORE dropping the cache. A protected route
       * (e.g. `/app`) only redirects from its `beforeLoad` guard, which runs on navigation,
       * never on a cache reset. Resetting identity to null in place leaves `AppArea`
       * rendering `null` — a blank screen the user is stuck on. Leaving the guarded tree
       * first is what actually gets them home.
       */
      await navigate({ to: '/$lang', params: { lang: lang ?? 'en' } });
      /*
       * Reset rather than invalidate: signing out must drop every cached response, not
       * merely mark them stale. A stale-but-present session list would still render.
       */
      await queryClient.resetQueries();
    },
  });

  return {
    signOut: () => {
      mutation.mutate();
    },
    isPending: mutation.isPending,
  };
}
